// Response schemas for Start here (plan §4 /start and /admin/induction, spec §5.8 and J6).
// Shapes follow apps/api/src/modules/induction (progress.ts, service.ts, quiz-service.ts,
// admin.ts). Unknown keys are dropped, so an attempt keeps only what the quiz screen shows: a
// stray answer key in an attempt-start answer never reaches the page (Q33).
import { ROLES } from '@cis/shared';
import { z } from 'zod';

// Text caps are the content caps times REDACTION_HEADROOM: for team roles the API swaps each
// Leads id ("R1", 2 chars) for "[Leads only]" (12 chars), so a legal 500-char title or a
// 20,000-char question can arrive longer. One long field must not fail the whole page (B3-R1).
const REDACTION_HEADROOM = 6;
const PACK_LABEL_MAX = 500;
const PACK_TEXT_MAX = 20_000;
const MAX_LABEL = PACK_LABEL_MAX * REDACTION_HEADROOM;
const MAX_TEXT = PACK_TEXT_MAX * REDACTION_HEADROOM;
const MAX_DATE = 10;
const MAX_INSTANT = 40;
const MAX_LESSON_NO = 99;
const MAX_QUESTION_NO = 255;
const MAX_OPTION_ID = 3;
// "No answer" is -1 in a scored attempt (quiz-service.ts).
const NO_ANSWER = -1;

const id = z.number().int().positive();
const count = z.number().int().min(0);
const lessonNo = z.number().int().min(1).max(MAX_LESSON_NO);
const questionNo = z.number().int().min(1).max(MAX_QUESTION_NO);
const date = z.string().max(MAX_DATE);

export const lessonProgressSchema = z.object({
  no: lessonNo,
  title: z.string().max(MAX_LABEL),
  minutes: z.number().int().min(0).nullable(),
  read: z.boolean(),
  readAt: z.string().max(MAX_INSTANT).nullable(),
});
export type LessonProgress = z.infer<typeof lessonProgressSchema>;

export const inductionCompletionSchema = z.object({
  completedOn: date,
  bestScore: count,
  attempts: count,
  source: z.string().max(MAX_LABEL),
});
export type InductionCompletion = z.infer<typeof inductionCompletionSchema>;

export const quizStatusSchema = z.object({
  available: z.boolean(),
  unlocked: z.boolean(),
  questions: count,
  passMark: count,
  attempts: count,
  bestScore: count.nullable(),
  openAttemptId: id.nullable(),
});
export type QuizStatus = z.infer<typeof quizStatusSchema>;

const dueSetBySchema = z.enum(['default', 'owner']);

export const inductionProgressSchema = z.object({
  user: z.object({ id, displayName: z.string().max(MAX_LABEL) }),
  dueOn: date,
  dueSetBy: dueSetBySchema,
  enrolmentVersion: count,
  overdue: z.boolean(),
  lessons: z.array(lessonProgressSchema),
  lessonsRead: count,
  lessonsTotal: count,
  nextLesson: lessonNo.nullable(),
  quiz: quizStatusSchema,
  completion: inductionCompletionSchema.nullable(),
});
export type InductionProgress = z.infer<typeof inductionProgressSchema>;

export const lessonListSchema = z.object({
  items: z.array(lessonProgressSchema),
  nextLesson: lessonNo.nullable(),
});
export type LessonList = z.infer<typeof lessonListSchema>;

/** One lesson; blocks stay `unknown` and the web block renderer checks each one (Q34). */
export const lessonSchema = z.object({
  no: lessonNo,
  title: z.string().max(MAX_LABEL),
  minutes: z.number().int().min(0).nullable(),
  blocks: z.array(z.unknown()),
  read: z.boolean(),
  prev: lessonNo.nullable(),
  next: lessonNo.nullable(),
});
export type Lesson = z.infer<typeof lessonSchema>;

export const quizOptionSchema = z.object({
  id: z.number().int().min(0).max(MAX_OPTION_ID),
  text: z.string().max(MAX_TEXT),
});
export type QuizOption = z.infer<typeof quizOptionSchema>;

export const attemptQuestionSchema = z.object({
  questionNo,
  text: z.string().max(MAX_TEXT),
  options: z.array(quizOptionSchema).min(1),
});
export type AttemptQuestion = z.infer<typeof attemptQuestionSchema>;

/** POST /start/quiz/attempts: the questions in the server's order, never the key. */
export const quizAttemptSchema = z.object({
  id,
  version: id,
  startedAt: z.string().max(MAX_INSTANT),
  passMark: count,
  questions: z.array(attemptQuestionSchema).min(1),
});
export type QuizAttempt = z.infer<typeof quizAttemptSchema>;

export const questionResultSchema = z.object({
  questionNo,
  text: z.string().max(MAX_TEXT),
  options: z.array(quizOptionSchema),
  chosen: z.number().int().min(NO_ANSWER).max(MAX_OPTION_ID),
  correct: z.number().int().min(0).max(MAX_OPTION_ID),
  isCorrect: z.boolean(),
  explanation: z.string().max(MAX_TEXT),
});
export type QuestionResult = z.infer<typeof questionResultSchema>;

/** PUT /start/quiz/attempts/:id: the score, the key and an explanation for every question. */
export const quizResultSchema = z.object({
  id,
  version: id,
  score: count,
  total: count,
  passMark: count,
  passed: z.boolean(),
  results: z.array(questionResultSchema),
  completion: inductionCompletionSchema.nullable(),
});
export type QuizResult = z.infer<typeof quizResultSchema>;

export interface QuizAnswer {
  readonly questionNo: number;
  readonly optionId: number;
}

export const completionRowSchema = z.object({
  user: z.object({ id, displayName: z.string().max(MAX_LABEL), role: z.enum(ROLES) }),
  dueOn: date,
  dueSetBy: dueSetBySchema,
  version: count,
  overdue: z.boolean(),
  lessonsRead: count,
  lessonsTotal: count,
  attempts: count,
  bestScore: count.nullable(),
  completedOn: date.nullable(),
});
export type CompletionRow = z.infer<typeof completionRowSchema>;

export const completionPageSchema = z.object({
  items: z.array(completionRowSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: count,
});
export type CompletionPage = z.infer<typeof completionPageSchema>;
